import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchBugDetail,
  loadReporter,
  postBugComment,
  rejectBug,
  validateBug,
  type BugDetail,
  type BugSummary,
  type BugsResponse,
  type SeenComments,
  type StatusDef,
} from "../core";
import { CloseIcon, StatusPill, TabBar, fullDate, relativeDate, type PanelTab } from "./chrome";

const PENDING = "PENDING_CLIENT_VALIDATION";
const VALIDATED = "VALIDATED";

/**
 * Vues de la liste : « À valider » et « À répondre » ne gardent que ce qui
 * attend le client, « Terminés » sort les bugs validés du flux courant.
 */
type ListFilter = "validate" | "reply" | "open" | "done";

interface TrackingPanelProps {
  trackerBase: string;
  apiKey: string;
  bugs: BugsResponse | null;
  bugsLoading: boolean;
  bugsError: string | null;
  reloadBugs: () => void;
  applyBugStatus: (bugId: string, status: string) => void;
  /** Bugs à valider + commentaires non lus — badge de l'onglet suivi. */
  badgeCount: number;
  /** bugId → date ISO du dernier commentaire de l'équipe déjà consulté. */
  seenComments: SeenComments;
  markSeen: (bugId: string, lastTeamCommentAt: string) => void;
  onSwitchTab: (tab: PanelTab) => void;
  onClose: () => void;
}

interface ValidateState {
  bugId: string | null;
  error: string | null;
  /** ids validés dans cette session, pour la confirmation inline. */
  done: Set<string>;
}

interface RejectState {
  /** Formulaire de refus ouvert sur ce bug. */
  openFor: string | null;
  comment: string;
  sending: boolean;
  error: string | null;
  /** ids refusés dans cette session, pour la confirmation inline. */
  done: Set<string>;
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function statusLabel(statuses: StatusDef[], value: string): string {
  return statuses.find((s) => s.value === value)?.label ?? value;
}

/** Un commentaire de l'équipe pas encore consulté sur ce navigateur ? */
export function hasUnseenTeamComment(bug: BugSummary, seen: SeenComments): boolean {
  if (!bug.lastTeamCommentAt) return false;
  const seenAt = seen[bug.id];
  return !seenAt || seenAt < bug.lastTeamCommentAt;
}

export function TrackingPanel(props: TrackingPanelProps) {
  const {
    trackerBase, apiKey, bugs, bugsLoading, bugsError,
    reloadBugs, applyBugStatus, seenComments, markSeen, onClose,
  } = props;

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BugDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [validate, setValidate] = useState<ValidateState>({ bugId: null, error: null, done: new Set() });
  const [reject, setReject] = useState<RejectState>({
    openFor: null, comment: "", sending: false, error: null, done: new Set(),
  });
  /** null tant que le client n'a pas choisi : vue par défaut calculée. */
  const [filter, setFilter] = useState<ListFilter | null>(null);
  const [commentText, setCommentText] = useState("");
  const [commentSending, setCommentSending] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Une seule tentative de re-fetch silencieux par ouverture quand une
  // capture renvoie une erreur (URL signée expirée).
  const refreshedForImage = useRef(false);

  // Recharger à l'ouverture du panneau (pas de polling).
  useEffect(() => {
    reloadBugs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDetail = useCallback(
    async (bugId: string) => {
      setDetailLoading(true);
      setDetailError(null);
      const result = await fetchBugDetail(trackerBase, apiKey, bugId);
      setDetailLoading(false);
      if (result.ok) {
        setDetail(result.data);
        // Consultation du fil : la pastille « nouveau » s'éteint pour ce bug.
        if (result.data.lastTeamCommentAt) {
          markSeen(bugId, result.data.lastTeamCommentAt);
        }
      } else {
        setDetailError(result.message);
      }
    },
    [trackerBase, apiKey, markSeen],
  );

  const openDetail = useCallback(
    (bugId: string, withReject = false) => {
      setDetailId(bugId);
      setDetail(null);
      setCommentText("");
      setCommentError(null);
      setValidate((v) => ({ ...v, error: null }));
      setReject((r) => ({ ...r, openFor: withReject ? bugId : null, comment: "", error: null }));
      void loadDetail(bugId);
    },
    [loadDetail],
  );

  const backToList = useCallback(() => {
    setDetailId(null);
    setDetail(null);
    setDetailError(null);
    setCommentError(null);
    setValidate((v) => ({ ...v, error: null }));
    setReject((r) => ({ ...r, openFor: null, comment: "", error: null }));
  }, []);

  const handleValidate = useCallback(
    async (bugId: string) => {
      setValidate((v) => ({ ...v, bugId, error: null }));
      const result = await validateBug(trackerBase, apiKey, bugId);
      if (result.ok) {
        // Le statut renvoyé (VALIDATED) est appliqué immédiatement : la carte
        // change de section et la confirmation s'affiche sans attendre le réseau.
        applyBugStatus(bugId, result.data.status);
        setDetail((d) => (d && d.id === bugId ? { ...d, status: result.data.status } : d));
        setValidate((v) => ({ bugId: null, error: null, done: new Set(v.done).add(bugId) }));
      } else {
        // 409 : le statut a changé entre-temps — on affiche et on recharge.
        setValidate((v) => ({ ...v, bugId: null, error: result.message }));
      }
      reloadBugs();
      if (detailId === bugId) void loadDetail(bugId);
    },
    [trackerBase, apiKey, reloadBugs, applyBugStatus, detailId, loadDetail],
  );

  const handleReject = useCallback(
    async (bugId: string) => {
      const comment = reject.comment.trim();
      if (!comment || reject.sending) return;
      setReject((r) => ({ ...r, sending: true, error: null }));
      const result = await rejectBug(
        trackerBase, apiKey, bugId, comment,
        loadReporter()?.name || undefined,
      );
      if (result.ok) {
        applyBugStatus(bugId, result.data.status);
        setDetail((d) => (d && d.id === bugId ? { ...d, status: result.data.status } : d));
        setReject((r) => ({
          openFor: null, comment: "", sending: false, error: null,
          done: new Set(r.done).add(bugId),
        }));
      } else {
        setReject((r) => ({ ...r, sending: false, error: result.message }));
      }
      reloadBugs();
      if (detailId === bugId) void loadDetail(bugId);
    },
    [trackerBase, apiKey, reject.comment, reject.sending, reloadBugs, applyBugStatus, detailId, loadDetail],
  );

  const handleComment = useCallback(async () => {
    const content = commentText.trim();
    if (!content || commentSending || !detailId) return;
    setCommentSending(true);
    setCommentError(null);
    const result = await postBugComment(
      trackerBase, apiKey, detailId, content,
      loadReporter()?.name || undefined,
    );
    setCommentSending(false);
    if (result.ok) {
      setCommentText("");
      // Ajout local immédiat, puis la liste (compteur) se resynchronise.
      setDetail((d) =>
        d && d.id === detailId
          ? { ...d, comments: [...d.comments, result.data], commentCount: d.commentCount + 1 }
          : d,
      );
      reloadBugs();
    } else {
      setCommentError(result.message);
    }
  }, [trackerBase, apiKey, detailId, commentText, commentSending, reloadBugs]);

  const handleImageError = useCallback(() => {
    if (refreshedForImage.current) return;
    refreshedForImage.current = true;
    reloadBugs();
    if (detailId) void loadDetail(detailId);
  }, [reloadBugs, detailId, loadDetail]);

  const statuses = bugs?.statuses ?? [];

  const rejectForm = (bug: { id: string }) => (
    <div className="bw-reject-zone">
      <span className="bw-reject-title">
        Qu&apos;est-ce qui ne fonctionne pas encore ?
      </span>
      <textarea
        className="bw-textarea bw-textarea-sm"
        value={reject.comment}
        maxLength={5_000}
        placeholder="Décrivez ce qui pose encore problème — votre message est transmis à l'équipe."
        onChange={(e) => setReject((r) => ({ ...r, comment: e.target.value }))}
      />
      {reject.error && <div className="bw-error">{reject.error}</div>}
      <div className="bw-row-actions">
        <button
          type="button"
          className="bw-btn bw-btn-sm bw-btn-danger"
          disabled={reject.sending || !reject.comment.trim()}
          onClick={() => void handleReject(bug.id)}
        >
          {reject.sending ? (
            <>
              <span className="bw-spinner" /> Envoi…
            </>
          ) : (
            "Refuser le correctif"
          )}
        </button>
        <button
          type="button"
          className="bw-btn bw-btn-sm bw-btn-ghost"
          disabled={reject.sending}
          onClick={() => setReject((r) => ({ ...r, openFor: null, comment: "", error: null }))}
        >
          Annuler
        </button>
      </div>
    </div>
  );

  /** Boutons Valider / Refuser d'un correctif en attente, + confirmations. */
  const decisionZone = (bug: { id: string; status: string }, small: boolean) => {
    if (validate.done.has(bug.id)) {
      return (
        <span className="bw-validate-ok">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          Correctif validé, merci !
        </span>
      );
    }
    if (reject.done.has(bug.id)) {
      return (
        <span className="bw-reject-ok">
          Refus transmis — l&apos;équipe reprend le ticket.
        </span>
      );
    }
    if (bug.status !== PENDING) return null;

    if (reject.openFor === bug.id && !small) return rejectForm(bug);

    const loading = validate.bugId === bug.id;
    return (
      <div className="bw-row-actions">
        <button
          type="button"
          className={"bw-btn" + (small ? " bw-btn-sm" : "")}
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            void handleValidate(bug.id);
          }}
        >
          {loading ? (
            <>
              <span className="bw-spinner" /> Validation…
            </>
          ) : (
            "Valider le correctif"
          )}
        </button>
        <button
          type="button"
          className={"bw-btn bw-btn-danger-ghost" + (small ? " bw-btn-sm" : "")}
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            // Le refus demande une explication : depuis la liste, on ouvre le
            // détail avec le formulaire de refus prêt.
            if (small) openDetail(bug.id, true);
            else setReject((r) => ({ ...r, openFor: bug.id, error: null }));
          }}
        >
          Refuser…
        </button>
      </div>
    );
  };

  const renderCard = (bug: BugSummary) => {
    const unseen = hasUnseenTeamComment(bug, seenComments);
    return (
      <div
        key={bug.id}
        className="bw-card"
        role="button"
        tabIndex={0}
        onClick={() => openDetail(bug.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDetail(bug.id);
          }
        }}
      >
        <div className="bw-card-top">
          <div className="bw-card-main">
            <span className="bw-card-title">{bug.title}</span>
            <div className="bw-card-meta">
              <StatusPill status={bug.status} label={statusLabel(statuses, bug.status)} />
              <span>{relativeDate(bug.createdAt)}</span>
              {bug.commentCount > 0 && (
                <span className={"bw-comment-count" + (unseen ? " bw-comment-count-new" : "")}>
                  <CommentIcon /> {bug.commentCount}
                  {unseen && <span className="bw-new-label">nouveau</span>}
                </span>
              )}
            </div>
            {unseen && bug.lastTeamCommentAt && (
              <span className="bw-card-new">
                Nouveau message de {bug.lastTeamCommentAuthor || "l'équipe"} ·{" "}
                {relativeDate(bug.lastTeamCommentAt)}
              </span>
            )}
          </div>
          {bug.screenshotUrl && (
            <img className="bw-thumb" src={bug.screenshotUrl} alt="" onError={handleImageError} />
          )}
        </div>
        {decisionZone(bug, true)}
      </div>
    );
  };

  const renderList = () => {
    if (bugsLoading && !bugs) {
      return (
        <>
          {[0, 1, 2].map((i) => (
            <div key={i} className="bw-skeleton">
              <div className="bw-skeleton-line" style={{ width: "70%" }} />
              <div className="bw-skeleton-line" style={{ width: "45%" }} />
            </div>
          ))}
        </>
      );
    }

    if (bugsError && !bugs) {
      return (
        <div className="bw-retry-zone">
          <div className="bw-error">{bugsError}</div>
          <button type="button" className="bw-btn bw-btn-sm" onClick={reloadBugs}>
            Réessayer
          </button>
        </div>
      );
    }

    if (!bugs || bugs.bugs.length === 0) {
      return (
        <div className="bw-empty">
          <InboxIcon />
          Aucun bug signalé pour l&apos;instant.
        </div>
      );
    }

    // Les bugs traités dans cette session restent visibles dans « À valider »
    // pour garder la confirmation inline sous les yeux.
    const handledNow = (b: BugSummary) => validate.done.has(b.id) || reject.done.has(b.id);
    const toValidate = bugs.bugs.filter((b) => b.status === PENDING || handledNow(b));
    const toReply = bugs.bugs.filter(
      (b) => b.awaitingReply && b.status !== PENDING && b.status !== VALIDATED && !handledNow(b),
    );
    const open = bugs.bugs.filter((b) => b.status !== VALIDATED);
    const done = bugs.bugs.filter((b) => b.status === VALIDATED);

    // Par défaut « À valider », sauf s'il n'y a rien à valider mais des
    // réponses attendues.
    const active: ListFilter =
      filter ?? (toValidate.length === 0 && toReply.length > 0 ? "reply" : "validate");

    const filters: { value: ListFilter; label: string; count: number; alert: boolean }[] = [
      { value: "validate", label: "À valider", count: toValidate.length, alert: toValidate.length > 0 },
      { value: "reply", label: "À répondre", count: toReply.length, alert: toReply.length > 0 },
      { value: "open", label: "En cours", count: open.length, alert: false },
      { value: "done", label: "Terminés", count: done.length, alert: false },
    ];

    const section = (key: string, title: string | null, group: BugSummary[], hint?: string) =>
      group.length === 0 ? null : (
        <div key={key} className="bw-track-section">
          {title && (
            <div className="bw-track-head">
              {title} <span className="bw-track-count">{group.length}</span>
            </div>
          )}
          {hint && <p className="bw-track-hint">{hint}</p>}
          {group.map(renderCard)}
        </div>
      );

    const empty = (text: string) => (
      <div className="bw-empty">
        <InboxIcon />
        {text}
      </div>
    );

    let content: React.ReactNode;
    if (active === "validate") {
      const allValidated =
        validate.done.size > 0 && !bugs.bugs.some((b) => b.status === PENDING);
      content =
        toValidate.length === 0 ? (
          empty("Aucun correctif à valider — l'équipe vous prévient dès qu'il y en a un.")
        ) : (
          <>
            {allValidated && (
              <div className="bw-track-banner bw-track-banner-ok">
                Tous les correctifs sont validés — merci !
              </div>
            )}
            {section(
              "validate",
              null,
              toValidate,
              "Vérifiez que le problème est corrigé, puis validez ou refusez.",
            )}
          </>
        );
    } else if (active === "reply") {
      content =
        toReply.length === 0
          ? empty("Aucune question en attente de votre réponse.")
          : section(
              "reply",
              null,
              toReply,
              "L'équipe vous a écrit en dernier — ouvrez le bug pour lui répondre.",
            );
    } else if (active === "open") {
      content =
        open.length === 0
          ? empty("Aucun bug en cours — tout est validé.")
          : statuses
              .filter((status) => status.value !== VALIDATED)
              .map((status) =>
                section(
                  status.value,
                  status.label,
                  open.filter((b) => b.status === status.value),
                ),
              );
    } else {
      content =
        done.length === 0
          ? empty("Aucun bug validé pour l'instant.")
          : section("done", null, done);
    }

    return (
      <>
        <div className="bw-filters" role="tablist" aria-label="Filtrer les bugs">
          {filters.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={active === f.value}
              className={
                "bw-filter" +
                (active === f.value ? " bw-filter-active" : "") +
                (f.alert ? " bw-filter-alert" : "")
              }
              onClick={() => setFilter(f.value)}
            >
              {f.label}
              <span className="bw-filter-count">{f.count}</span>
            </button>
          ))}
        </div>
        {validate.error && <div className="bw-error">{validate.error}</div>}
        {content}
      </>
    );
  };

  const renderComments = (bug: BugDetail) => (
    <div className="bw-track-section">
      <div className="bw-track-head">
        Échanges{" "}
        {bug.comments.length > 0 && (
          <span className="bw-track-count">{bug.comments.length}</span>
        )}
      </div>
      {bug.comments.length === 0 && (
        <p className="bw-comments-empty">
          Aucun message pour l&apos;instant — une question, une précision ?
          Écrivez à l&apos;équipe ci-dessous.
        </p>
      )}
      {bug.comments.length > 0 && (
        <div className="bw-comments">
          {bug.comments.map((comment) => (
            <div
              key={comment.id}
              className={"bw-comment " + (comment.fromTeam ? "bw-comment-team" : "bw-comment-client")}
            >
              <div className="bw-comment-meta">
                <strong>
                  {comment.fromTeam
                    ? comment.authorName || "L'équipe"
                    : comment.authorName || "Vous"}
                </strong>
                {comment.fromTeam && <span className="bw-comment-tag">équipe</span>}
                <span>{relativeDate(comment.createdAt)}</span>
              </div>
              <div className="bw-comment-body">{comment.content}</div>
            </div>
          ))}
        </div>
      )}
      <div className="bw-comment-form">
        <textarea
          className="bw-textarea bw-textarea-sm"
          value={commentText}
          maxLength={5_000}
          placeholder="Répondre à l'équipe…"
          onChange={(e) => setCommentText(e.target.value)}
        />
        {commentError && <div className="bw-error">{commentError}</div>}
        <div className="bw-row-actions">
          <button
            type="button"
            className="bw-btn bw-btn-sm"
            disabled={commentSending || !commentText.trim()}
            onClick={() => void handleComment()}
          >
            {commentSending ? (
              <>
                <span className="bw-spinner" /> Envoi…
              </>
            ) : (
              "Envoyer"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (detailLoading && !detail) {
      return (
        <div className="bw-skeleton">
          <div className="bw-skeleton-line" style={{ width: "60%" }} />
          <div className="bw-skeleton-line" style={{ width: "90%" }} />
          <div className="bw-skeleton-line" style={{ width: "80%" }} />
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="bw-retry-zone">
          <div className="bw-error">{detailError}</div>
          <button type="button" className="bw-btn bw-btn-sm" onClick={() => detailId && void loadDetail(detailId)}>
            Réessayer
          </button>
        </div>
      );
    }

    if (!detail) return null;

    // Timeline du plus récent au plus ancien.
    const history = [...detail.statusHistory].reverse();

    return (
      <>
        <div className="bw-detail-title">{detail.title}</div>
        <div className="bw-card-meta">
          <StatusPill status={detail.status} label={statusLabel(statuses, detail.status)} />
          <span>{relativeDate(detail.createdAt)}</span>
        </div>

        {validate.error && <div className="bw-error">{validate.error}</div>}
        {decisionZone(detail, false)}

        {detail.description && <p className="bw-detail-desc">{detail.description}</p>}

        {detail.screenshotUrl && (
          <button
            type="button"
            className="bw-detail-shot"
            onClick={() => setZoomUrl(detail.screenshotUrl)}
            aria-label="Agrandir la capture d'écran"
          >
            <img src={detail.screenshotUrl} alt="Capture d'écran du bug" onError={handleImageError} />
          </button>
        )}

        <div className="bw-detail-meta">
          <span>
            <strong>Page concernée :</strong>{" "}
            <span className="bw-page-url">{detail.pageUrl}</span>
          </span>
          {detail.reporterName && (
            <span>
              <strong>Signalé par :</strong> {detail.reporterName}
            </span>
          )}
        </div>

        {renderComments(detail)}

        {history.length > 0 && (
          <div className="bw-track-section">
            <div className="bw-track-head">Historique</div>
            <div className="bw-timeline">
              {history.map((change, i) => (
                <div key={i} className="bw-timeline-item">
                  <div className="bw-timeline-rail">
                    <div className="bw-timeline-dot" />
                    {i < history.length - 1 && <div className="bw-timeline-line" />}
                  </div>
                  <div className="bw-timeline-body">
                    <div>
                      <StatusPill status={change.toStatus} label={statusLabel(statuses, change.toStatus)} />
                    </div>
                    <div className="bw-timeline-when">
                      {change.fromStatus === null
                        ? "Signalement créé"
                        : (change.changedByLabel ?? "Vous")}{" "}
                      · {fullDate(change.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="bw-panel" role="dialog" aria-label="Suivi des bugs">
      <div className="bw-panel-header">
        <span className="bw-panel-title">
          {bugs?.project.name ? `Suivi — ${bugs.project.name}` : "Suivi des bugs"}
        </span>
        <button type="button" className="bw-close" onClick={onClose} aria-label="Fermer">
          <CloseIcon />
        </button>
      </div>

      <TabBar active="tracking" onChange={props.onSwitchTab} badgeCount={props.badgeCount} />

      <div className="bw-panel-body">
        {detailId ? (
          <>
            <button type="button" className="bw-back" onClick={backToList}>
              <BackIcon /> Retour à la liste
            </button>
            {renderDetail()}
          </>
        ) : (
          renderList()
        )}
      </div>

      {zoomUrl && (
        <div className="bw-zoom" onClick={() => setZoomUrl(null)} role="presentation">
          <img src={zoomUrl} alt="Capture d'écran agrandie" />
        </div>
      )}
    </div>
  );
}
